import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { extractOwnerAndBrokerFilters } from '@/utils/typesense/util';
import React, { useEffect } from 'react';
import { Configure, Index, useInstantSearch } from 'react-instantsearch';
import { useSelector } from 'react-redux';
import { taskSearch } from '@/utils/typesense/clients/taskSearch';
import { buildTypesenseInFilter } from '@/utils/typesense/util';

const RELATED_ENTITY_HITS = 10;

const federatedTaskSearchByRelatedEntities = async ({
	searchText,
	uiState,
	indexName,
	hitsPerPage = 30,
}) => {
	if (!searchText?.trim()) return [];
	const filters = uiState?.[indexName]?.configure?.filters;

	const getRelatedSearch = (relatedIndexName) => {
		const scopeFilters = extractOwnerAndBrokerFilters(filters, relatedIndexName);
		return {
			indexName: relatedIndexName,
			params: {
				query: searchText,
				hitsPerPage: RELATED_ENTITY_HITS,
				...(scopeFilters && { filters: scopeFilters }),
			},
		};
	};
	// 1) Search clients + deals within the tasks table's broker group / client owner scope
	const [clientRes, dealRes] = await Promise.all([
		clientSearch.search([getRelatedSearch('clients')]),
		dealSearch.search([getRelatedSearch('deals')]),
	]);

	const clientIds = (clientRes?.results?.[0]?.hits ?? []).map((h) => h.id);
	const dealIds = (dealRes?.results?.[0]?.hits ?? []).map((h) => h.id);

	if (clientIds.length === 0 && dealIds.length === 0) return [];

	const relatedClauses = [
		clientIds.length > 0 && buildTypesenseInFilter('clientId', clientIds),
		dealIds.length > 0 && buildTypesenseInFilter('dealId', dealIds),
	]
		.filter(Boolean)
		.join(' || ');

	const combinedFilters = [filters, relatedClauses.length > 0 && `(${relatedClauses})`]
		.filter(Boolean)
		.join(' && ');

	return await taskSearch.search([
		{
			indexName: 'tasks',
			params: {
				query: '*',
				filters: combinedFilters,
				hitsPerPage,
				...(uiState?.[indexName]?.sortBy && {
					sortBy: uiState[indexName].sortBy,
				}),
			},
		},
	]);
};

const GlobalSearchHelper = ({ setSearchItems = (items) => {} }) => {
	const searchText = useSelector((state) => state.instantSearch.searchText);
	const { uiState } = useInstantSearch();
	const indexName = Object.keys(uiState)?.[0];

	// Searches tasks by their related clients and deals. Client and deal ids
	// (insightId/claimId) are found by the normal search (query_by).
	useEffect(() => {
		if (!searchText || !uiState?.tasks) {
			setSearchItems([]);
			return;
		}

		const searchTasks = async () => {
			const res = await federatedTaskSearchByRelatedEntities({
				searchText,
				uiState,
				indexName,
			});
			setSearchItems(res?.results?.[0]?.hits ?? []);
		};

		searchTasks();
	}, [searchText, uiState?.[indexName]]);

	return <></>;
};

GlobalSearchHelper.displayName = 'GlobalSearchHelper';
export default GlobalSearchHelper;
